<template>
  <PVButton
    ref="buttonRef"
    :data-testid="dataTestid"
    :label="label"
    :disabled="isButtonBusyOrDisabled"
    :loading="busy"
    :pt="passThroughProps"
    :as="as"
    v-bind="routingProps"
    unstyled
  >
    <span
      v-if="isButtonBusyOrDisabled"
      class="disabled-overlay"
      aria-hidden="true"
    >
      <span
        v-if="busy"
        class="spinner-border spinner-border-sm"
        :class="spinnerVariantClass"
      />
    </span>
    <slot name="icon">
      <Icon
        v-if="icon"
        :class="{
          'mr-2': !isIconOnly,
        }"
        :icon="icon"
        :fixed-width="isIconOnly"
      />
    </slot>
    <slot>
      <!-- Supply default slot content so that {@link file://./StandardButtonWithFreeformDropdown.vue}
      can render the button label in the dropdown content when no default slot content is provided. -->
      {{ label }}
    </slot>
  </PVButton>
</template>
<script lang="ts">
export type StandardButtonProps = {
  /**
   * The label to be displayed in the button.
   */
  label?: string;
  /**
   * If true, this button will be disabled and a loading spinner will be shown
   */
  busy?: boolean;
  disabled?: boolean;
  /**
   * If true, the button will span the full width of its container
   */
  block?: boolean;
  /**
   * If true, the button will have rounded pill styling
   */
  pill?: boolean;
  /**
   * If true, the button will appear in pressed/active state
   */
  pressed?: boolean;
  /**
   * Button size: 'sm', 'lg' and '' for medium
   */
  size?: 'sm' | 'lg' | '';
  /**
   * Button variant: 'primary', 'secondary', 'info', 'success', 'warning', 'danger', etc
   * For reference, primeVue's alias for `variant` is `severity`.
   */
  variant?: string;
  /**
   * Icon to be displayed before `<slot />`.
   *
   * This is only for simple use cases. If you need to further customize the icon, use the `icon` slot instead.
   */
  icon?: string;
  dataTestid?: string;
  to?: RouteLocationRaw;
  href?: string;
  classProp?: Record<string, any> | string | string[];
};

export default {
  name: 'StandardButton',
  compatConfig: { MODE: 3 },
};
</script>
<script setup lang="ts">
import { ref, computed, useSlots } from 'vue';
import { hasRenderedSlotContent } from '@/utils/vueSlots';
import type { ButtonPassThroughOptions } from 'primevue/button';
import { PassThrough } from '@primevue/core';
import type { RouteLocationRaw } from 'vue-router';

const props = defineProps<StandardButtonProps>();

const slots = useSlots();

/**
 * Does NOT expose `href` prop because otherwise it will conflict with the `to` prop.
 *
 * If you need to use `href`, use a `b-button` instead.
 */
const buttonRef = ref<{ $el: HTMLElement } | null>(null);

const isButtonBusyOrDisabled = computed(() => props.busy || props.disabled);

// Map size to Bootstrap size classes
const sizeClass = computed(() => {
  if (props.size === 'sm') return 'btn-sm';

  if (props.size === 'lg') return 'btn-lg';

  return ''; // Default to medium
});

// Map variant to Bootstrap variant classes
const variantClass = computed(() => {
  const variant = props.variant;
  if (!variant) return 'btn-secondary'; // Default to secondary

  return `btn-${variant}`;
});

/**
 * Build PassThrough props for PrimeVue unstyled mode.
 * Applies Bootstrap button classes to match BootstrapVue styling.
 *
 * Details: https://primevue.org/passthrough/
 */
const passThroughProps = computed((): PassThrough<ButtonPassThroughOptions> => {
  const classes = [
    'btn',
    'standard-button',
    variantClass.value,
    sizeClass.value,
    props.block ? 'btn-block' : '',
    props.pill ? 'rounded-pill' : '',
    props.pressed ? 'active' : '',
    isButtonBusyOrDisabled.value ? 'disabled position-relative' : '',
    props.classProp,
  ].filter(Boolean);

  return {
    root: {
      class: classes,
    },
  };
});

/**
 * Spinner sits on a light overlay (like the former b-overlay), so it uses
 * text-{color} rather than inheriting the button foreground color.
 */
const spinnerVariantClass = computed((): string => {
  const variant = props.variant ?? 'secondary';

  if (variant.startsWith('outline-')) {
    return `text-${variant.slice('outline-'.length)}`;
  }

  return `text-${variant}`;
});

/**
 * Check if the component is rendering in icon-only mode.
 */
const isIconOnly = computed((): boolean => {
  const hasDefaultSlotContent = slots.default != null && hasRenderedSlotContent(slots.default());
  return !hasDefaultSlotContent && (props.icon != null || slots.icon != null);
});

/**
 * Only set `as` for non-button tags. Passing `as="button"` makes PrimeVue
 * ignore `unstyled` (https://github.com/primefaces/primevue/issues/8404).
 * Omitting it keeps the default `'BUTTON'`, which respects unstyled.
 */
const as = computed(() => {
  if (isButtonBusyOrDisabled.value) return undefined;

  if (props.to) return 'router-link';

  if (props.href) return 'a';

  return undefined;
});

// Omit the `href` prop entirely if `to` is passed. Passing `href` as undefined (vs not passing it at all) to
// router-link will cause it to not generate a `href` property from the `to` prop.
const routingProps = computed(() => {
  if (props.to) {
    return {
      to: props.to,
    };
  }

  if (props.href) {
    return {
      href: props.href,
    };
  }

  return {};
});

const focus = () => {
  buttonRef.value?.$el.focus();
};

defineExpose({
  focus,
});
</script>

<style scoped lang="scss">
.standard-button {
  &.disabled {
    opacity: 1; // override the default opacity of 0.6, since we are using a background color overlay
  }

  .disabled-overlay {
    position: absolute;
    inset: -1px; // -1px to account for the button border
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.6);
    border-radius: inherit;
  }
}
</style>
